import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { firebaseApp } from '../firebase';
import GoogleSignInButton from './GoogleSignInButton';

/** When false, only Google is shown; email/password handlers and verification UI stay in this file for later. */
const SHOW_EMAIL_PASSWORD_AUTH = false;

function VerificationCodeStep({
  subtitle,
  code,
  onCodeChange,
  resendFeedback,
  error,
  submitting,
  onConfirm,
  onResend,
}) {
  return (
    <div className="auth-modal-notice" role="status">
      <p className="auth-card__lede" style={{ marginTop: 0 }}>
        {subtitle}
      </p>
      {error ? (
        <p className="auth-modal-error" role="alert" style={{ marginTop: '0.5rem' }}>
          {error}
        </p>
      ) : null}
      {resendFeedback ? <p>{resendFeedback}</p> : null}
      <div className="auth-form" style={{ marginTop: '0.65rem' }}>
        <label className="auth-field">
          <span className="auth-field__label">6-digit code</span>
          <input
            className="auth-field__input"
            name="verificationCode"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
            disabled={submitting}
            aria-label="Verification code"
          />
        </label>
      </div>
      <div className="auth-modal-notice__actions">
        <button type="button" className="auth-submit" disabled={submitting || code.length !== 6} onClick={onConfirm}>
          {submitting ? 'Verifying…' : 'Verify and continue'}
        </button>
        <button type="button" className="auth-submit" disabled={submitting} onClick={onResend}>
          {submitting ? 'Sending…' : 'Resend code'}
        </button>
      </div>
      <p className="auth-card__lede" style={{ marginBottom: 0, fontSize: '0.76rem', opacity: 0.85 }}>
        Codes expire after 15 minutes.
      </p>
    </div>
  );
}

function AuthModal() {
  const {
    authModalOpen,
    authModalMode,
    setAuthModalMode,
    closeAuth,
    login,
    loginWithGoogle,
    signup,
    sendVerificationCode,
    confirmEmailVerification,
    signOutFirebaseOnly,
  } = useAuth();
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signupCompleteUi, setSignupCompleteUi] = useState(false);
  const [signInNeedsVerify, setSignInNeedsVerify] = useState(false);
  const [signupCodeEmailed, setSignupCodeEmailed] = useState(null);
  const [pendingSignupUsername, setPendingSignupUsername] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyPanelError, setVerifyPanelError] = useState('');
  const [resendFeedback, setResendFeedback] = useState('');

  const awaitingCodeGate = signInNeedsVerify || signupCompleteUi;

  const dismissModal = useCallback(async () => {
    if (awaitingCodeGate) {
      await signOutFirebaseOnly();
      setSignInNeedsVerify(false);
      setSignupCompleteUi(false);
      setVerificationCode('');
      setPendingSignupUsername('');
      setResendFeedback('');
      setVerifyPanelError('');
      setSignupCodeEmailed(null);
    }
    closeAuth();
  }, [awaitingCodeGate, signOutFirebaseOnly, closeAuth]);

  useEffect(() => {
    if (!authModalOpen) {
      setFormError('');
      setVerifyPanelError('');
      setSubmitting(false);
      setSignupCodeEmailed(null);
      setSignupCompleteUi(false);
      setSignInNeedsVerify(false);
      setPendingSignupUsername('');
      setVerificationCode('');
      setResendFeedback('');
    }
  }, [authModalOpen, authModalMode]);

  useEffect(() => {
    if (!authModalOpen) {
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [authModalOpen]);

  useEffect(() => {
    if (!authModalOpen) {
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === 'Escape') {
        void dismissModal();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [authModalOpen, dismissModal]);

  async function handleSignIn(e) {
    e.preventDefault();
    setFormError('');
    if (!firebaseApp) {
      setFormError('Firebase is not configured.');
      return;
    }
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get('email') || '').trim();
    const password = String(fd.get('password') || '');
    if (!email || !password) {
      setFormError('Enter email and password.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result.needsVerification) {
        setVerificationCode('');
        setVerifyPanelError('');
        setResendFeedback('');
        setSignInNeedsVerify(true);
      } else {
        closeAuth();
      }
    } catch (err) {
      setFormError(err.message || 'Sign in failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogle() {
    setFormError('');
    setSubmitting(true);
    try {
      await loginWithGoogle();
      closeAuth();
    } catch (err) {
      setFormError(err.message || 'Google sign-in failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignUp(e) {
    e.preventDefault();
    setFormError('');
    if (!firebaseApp) {
      setFormError('Firebase is not configured.');
      return;
    }
    const fd = new FormData(e.currentTarget);
    const username = String(fd.get('username') || '').trim();
    const email = String(fd.get('email') || '').trim();
    const password = String(fd.get('password') || '');
    const confirmPassword = String(fd.get('confirmPassword') || '');
    if (!username || !email || !password) {
      setFormError('Fill in all fields.');
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setFormError('Username may only contain letters, numbers, and underscores.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError('Enter a valid email address.');
      return;
    }
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await signup({ username, email, password });
      setPendingSignupUsername(username);
      setSignupCodeEmailed(result.emailed);
      setVerificationCode('');
      setVerifyPanelError('');
      setResendFeedback('');
      setSignupCompleteUi(true);
    } catch (err) {
      setFormError(err.message || 'Sign up failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResendCode() {
    setVerifyPanelError('');
    setResendFeedback('');
    setSubmitting(true);
    try {
      await sendVerificationCode(
        pendingSignupUsername ? { username: pendingSignupUsername } : {}
      );
      setResendFeedback('We sent another code to your email.');
      setSignupCodeEmailed(true);
    } catch (err) {
      setVerifyPanelError(err.message || 'Could not send code');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirmCode() {
    setVerifyPanelError('');
    setResendFeedback('');
    setSubmitting(true);
    try {
      await confirmEmailVerification(verificationCode);
      closeAuth();
    } catch (err) {
      setVerifyPanelError(err.message || 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (!authModalOpen) {
    return null;
  }

  const isSignin = authModalMode === 'signin';

  return (
    <div
      className="auth-modal-root"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          void dismissModal();
        }
      }}
    >
      <div
        className="auth-modal-dialog auth-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button type="button" className="auth-modal-close" aria-label="Close" onClick={() => void dismissModal()}>
          ×
        </button>

        {formError && !signupCompleteUi && !signInNeedsVerify ? (
          <p className="auth-modal-error" role="alert">
            {formError}
          </p>
        ) : null}

        {isSignin ? (
          <>
            {!signInNeedsVerify ? (
              <header className="auth-card__header">
                <p className="auth-card__eyebrow">Welcome back</p>
                <h1 id="auth-modal-title" className="auth-card__title">
                  Sign in
                </h1>
                <p className="auth-card__lede">
                  {SHOW_EMAIL_PASSWORD_AUTH
                    ? 'Email and password use Firebase Authentication (same as Google).'
                    : 'Sign in with your Google account.'}
                </p>
              </header>
            ) : (
              <header className="auth-card__header">
                <p className="auth-card__eyebrow">Verify email</p>
                <h1 id="auth-modal-title" className="auth-card__title">
                  Enter code
                </h1>
              </header>
            )}

            {signInNeedsVerify ? (
              <VerificationCodeStep
                subtitle="We emailed you a 6-digit code. Enter it below to finish signing in — you can&apos;t use the app until this step is complete."
                code={verificationCode}
                onCodeChange={setVerificationCode}
                error={verifyPanelError}
                resendFeedback={resendFeedback}
                submitting={submitting}
                onConfirm={() => void handleConfirmCode()}
                onResend={() => void handleResendCode()}
              />
            ) : (
              <>
                {firebaseApp ? <GoogleSignInButton onClick={handleGoogle} disabled={submitting} /> : null}

                {SHOW_EMAIL_PASSWORD_AUTH ? (
                  <>
                    {firebaseApp ? (
                      <p className="auth-divider" aria-hidden="true">
                        <span>or</span>
                      </p>
                    ) : null}

                    <form className="auth-form" onSubmit={handleSignIn} noValidate>
                      <label className="auth-field">
                        <span className="auth-field__label">Email</span>
                        <input
                          className="auth-field__input"
                          name="email"
                          type="email"
                          autoComplete="email"
                          placeholder=" "
                          disabled={submitting}
                        />
                      </label>
                      <label className="auth-field">
                        <span className="auth-field__label">Password</span>
                        <input
                          className="auth-field__input"
                          name="password"
                          type="password"
                          autoComplete="current-password"
                          placeholder=" "
                          disabled={submitting}
                        />
                      </label>
                      <button type="submit" className="auth-submit" disabled={submitting}>
                        {submitting ? 'Signing in…' : 'Sign in'}
                      </button>
                    </form>

                    <p className="auth-footer">
                      New here?{' '}
                      <button
                        type="button"
                        className="auth-link auth-link--button"
                        onClick={() => setAuthModalMode('signup')}
                        disabled={submitting}
                      >
                        Create an account
                      </button>
                    </p>
                  </>
                ) : null}
              </>
            )}
          </>
        ) : signupCompleteUi ? (
          <>
            <header className="auth-card__header">
              <p className="auth-card__eyebrow">Verify email</p>
              <h1 id="auth-modal-title" className="auth-card__title">
                Enter code
              </h1>
            </header>
            <VerificationCodeStep
              subtitle={
                signupCodeEmailed
                  ? 'We sent a 6-digit code to your email. Enter it to create your stans-world account — you cannot continue without it.'
                  : 'We could not reach your mail server from here (missing SMTP config or relay error). Configure SMTP on the API or read the verification code printed in the server console, then enter it below.'
              }
              code={verificationCode}
              onCodeChange={setVerificationCode}
              error={verifyPanelError}
              resendFeedback={resendFeedback}
              submitting={submitting}
              onConfirm={() => void handleConfirmCode()}
              onResend={() => void handleResendCode()}
            />
          </>
        ) : (
          <>
            <header className="auth-card__header">
              <p className="auth-card__eyebrow">Join stans-world</p>
              <h1 id="auth-modal-title" className="auth-card__title">
                Create account
              </h1>
              <p className="auth-card__lede">
                {SHOW_EMAIL_PASSWORD_AUTH
                  ? 'Choose a username; email and password are stored with Firebase.'
                  : 'Continue with Google to create your account.'}
              </p>
            </header>

            {firebaseApp ? <GoogleSignInButton onClick={handleGoogle} disabled={submitting} /> : null}

            {SHOW_EMAIL_PASSWORD_AUTH ? (
              <>
                {firebaseApp ? (
                  <p className="auth-divider" aria-hidden="true">
                    <span>or</span>
                  </p>
                ) : null}

                <form className="auth-form" onSubmit={handleSignUp} noValidate>
                  <label className="auth-field">
                    <span className="auth-field__label">Username</span>
                    <input
                      className="auth-field__input"
                      name="username"
                      type="text"
                      autoComplete="username"
                      placeholder=" "
                      disabled={submitting}
                    />
                  </label>
                  <label className="auth-field">
                    <span className="auth-field__label">Email</span>
                    <input
                      className="auth-field__input"
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder=" "
                      disabled={submitting}
                    />
                  </label>
                  <label className="auth-field">
                    <span className="auth-field__label">Password</span>
                    <input
                      className="auth-field__input"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      placeholder=" "
                      disabled={submitting}
                    />
                  </label>
                  <label className="auth-field">
                    <span className="auth-field__label">Confirm password</span>
                    <input
                      className="auth-field__input"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      placeholder=" "
                      disabled={submitting}
                    />
                  </label>
                  <button type="submit" className="auth-submit" disabled={submitting}>
                    {submitting ? 'Creating…' : 'Create account'}
                  </button>
                </form>

                <p className="auth-footer">
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="auth-link auth-link--button"
                    onClick={() => setAuthModalMode('signin')}
                    disabled={submitting}
                  >
                    Sign in
                  </button>
                </p>
              </>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export default AuthModal;
